/* =====================================================================
   assets/soi-presell.js · pre-sell and quiz behaviour (M10, 03 §4 to §8)
   Loaded only by layout/soi-funnel.liquid. No dependencies, no network requests,
   no cookies; the quiz keeps its answers in sessionStorage only.
   ===================================================================== */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desk = window.matchMedia('(min-width: 768px)');

  /* ---------- P-HEADER + P-STICKY ---------- */
  class SoiPresellHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-presell-bar]');
      this.sticky = this.querySelector('[data-presell-sticky]');
      this.mode = this.dataset.stickyMode || 'first_paint';
      if (!this.bar && !this.sticky) return;
      this.heroGone = false;
      this.triggerPassed = this.mode === 'first_paint';
      this.ctaVisible = new Set();
      this.footerVisible = false;

      const hero = document.querySelector('[data-presell-hero]');
      if (hero) {
        new IntersectionObserver((es) => {
          es.forEach((e) => { this.heroGone = !e.isIntersecting && e.boundingClientRect.top < 0; });
          this.update();
        }).observe(hero);
      }
      const heroCta = hero && hero.querySelector('[data-presell-cta]');
      if (this.mode === 'after_hero' && heroCta) {
        new IntersectionObserver((es) => {
          es.forEach((e) => { this.triggerPassed = e.intersectionRatio < 0.5; });
          this.update();
        }, { threshold: [0, 0.5, 1] }).observe(heroCta);
      } else if (this.mode === 'after_hero') {
        this.triggerPassed = true;
      }
      const trig = document.querySelector('[data-presell-sticky-trigger]');
      if (this.mode === 'after_trigger' && trig) {
        new IntersectionObserver((es) => {
          es.forEach((e) => { this.triggerPassed = e.boundingClientRect.bottom < window.innerHeight * 0.5 || (!e.isIntersecting && e.boundingClientRect.top < 0); });
          this.update();
        }, { threshold: [0, 0.25, 0.5, 0.75, 1] }).observe(trig);
      }
      /* The bar hides only when it would actually cover or duplicate an in-content CTA (A6-R2-04, A6-R3-02):
         1. any in-content CTA inside the bottom bar zone (root shrunk to the lowest 140 px of the viewport);
         2. the offer box body (tiers + CTA) and the full buttons of the offer, rows, article and closing CTAs while
            ≥ 50 % of them is visible anywhere in the viewport (03 §4.2), so two identical terracotta buttons are
            never on screen at once. Text links (.soi-pre-textcta, .soi-pre-interrupt) only count under rule 1.
         Everything inside [data-presell-hero] is excluded, so the first-paint sticky (07 A6 c2) still shows. */
      this.ctaCovered = new Set();
      /* the full offer box counts from its body (tiers, CTA): its image may pass under the bar */
      /* row CTA wrappers count by their links only: the guarantee line under the link is not a control */
      const inContent = [...document.querySelectorAll('[data-presell-cta]:not(.soi-pre-row__cta), .soi-pre-row__cta a, .soi-pre-textcta')]
        .filter((el) => !el.closest('[data-presell-hero]'))
        .map((el) => el.querySelector(':scope > .soi-pre-offer__body') || el);
      const big = [...document.querySelectorAll('.soi-pre-offer__cta, .soi-pre-offer__body, .soi-pre-row__cta .soi-btn, .soi-pre-cta .soi-btn, .soi-pre-close .soi-btn')]
        .filter((el) => !el.closest('[data-presell-hero]'));
      /* rule 2: ratio of the element inside the full viewport; a box taller than the viewport counts once it fills half of it */
      const halfIo = new IntersectionObserver((es) => {
        es.forEach((e) => {
          const h = e.boundingClientRect.height || 1;
          const fills = e.intersectionRect.height >= Math.min(h, window.innerHeight) * 0.5;
          if (e.isIntersecting && (e.intersectionRatio >= 0.5 || fills)) this.ctaVisible.add(e.target); else this.ctaVisible.delete(e.target);
        });
        this.update();
      }, { rootMargin: '0px', threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
      big.forEach((el) => halfIo.observe(el));
      const buildZone = () => {
        if (this.zoneIo) this.zoneIo.disconnect();
        this.ctaCovered.clear();
        this.zoneIo = new IntersectionObserver((es) => {
          es.forEach((e) => { if (e.isIntersecting) this.ctaCovered.add(e.target); else this.ctaCovered.delete(e.target); });
          this.update();
        }, { rootMargin: `-${Math.max(0, window.innerHeight - 140)}px 0px 0px 0px`, threshold: [0] });
        inContent.forEach((el) => this.zoneIo.observe(el));
      };
      buildZone();
      let rz = 0;
      window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(buildZone, 150); }, { passive: true });
      const foot = document.querySelector('[data-presell-footer]');
      if (foot) {
        new IntersectionObserver((es) => { es.forEach((e) => { this.footerVisible = e.isIntersecting; }); this.update(); }).observe(foot);
      }
      desk.addEventListener('change', () => this.update());
      this.update();
    }
    setOn(el, on) {
      if (!el) return;
      el.classList.toggle('is-on', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
      el.querySelectorAll('a, button').forEach((a) => { a.tabIndex = on ? 0 : -1; });
    }
    update() {
      const blocked = this.ctaVisible.size > 0 || (this.ctaCovered && this.ctaCovered.size > 0) || this.footerVisible;
      this.setOn(this.bar, desk.matches && this.heroGone);
      const showSticky = !desk.matches && this.triggerPassed && !blocked;
      this.setOn(this.sticky, showSticky);
      document.body.classList.toggle('has-pre-sticky', !!this.sticky && this.triggerPassed);
    }
  }
  if (!customElements.get('soi-presell-header')) customElements.define('soi-presell-header', SoiPresellHeader);

  /* ---------- P-OFFER: tier rows rewrite the CTA's tier= parameter (inert since 2026-10-14: no tier rows rendered) ---------- */
  class SoiPresellOffer extends HTMLElement {
    connectedCallback() {
      const cta = this.querySelector('[data-offer-cta]');
      if (!cta) return;
      this.addEventListener('change', (e) => {
        const r = e.target;
        if (!r.matches('input[type="radio"]')) return;
        try {
          const u = new URL(cta.getAttribute('href'), window.location.origin);
          u.searchParams.set('tier', r.value);
          cta.setAttribute('href', u.pathname + u.search + u.hash);
        } catch (err) { /* keep the server href */ }
      });
    }
  }
  if (!customElements.get('soi-presell-offer')) customElements.define('soi-presell-offer', SoiPresellOffer);

  /* ---------- P2-SELFCHECK: local only ---------- */
  class SoiSelfCheck extends HTMLElement {
    connectedCallback() {
      this.out = this.querySelector('[data-self-result]');
      this.items = [...this.querySelectorAll('[data-self-item]')];
      this.addEventListener('change', () => this.update());
    }
    update() {
      const n = this.items.filter((i) => i.checked).length;
      const total = this.items.length;
      let t = this.dataset.r0;
      if (n >= total) t = this.dataset.r5;
      else if (n >= 3) t = this.dataset.r3;
      if (this.out) this.out.textContent = `${n} von ${total}: ${t}`;
    }
  }
  if (!customElements.get('soi-self-check')) customElements.define('soi-self-check', SoiSelfCheck);

  /* ---------- Cookie-Einstellungen in the funnel footer ---------- */
  const cookieRow = document.querySelector('[data-presell-cookie-row]');
  const cookieBtn = document.querySelector('[data-presell-cookie]');
  const cookieCheck = () => {
    if (cookieRow && window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') cookieRow.hidden = false;
  };
  if (cookieBtn) cookieBtn.addEventListener('click', () => window.privacyBanner && window.privacyBanner.showPreferences());
  cookieCheck();
  window.addEventListener('load', cookieCheck, { once: true });
  document.addEventListener('visitorConsentCollected', cookieCheck);

  /* ---------- Ritual-Finder quiz (03 §8) ---------- */
  const STORE = 'soi_quiz';
  class SoiQuiz extends HTMLElement {
    connectedCallback() {
      try { this.cfg = JSON.parse(this.querySelector('[data-quiz-config]').textContent); } catch (e) { this.cfg = { picks: {}, names: {}, defaults: {}, types: {}, doors: {} }; }
      this.steps = [...this.querySelectorAll('[data-quiz-step]')];
      this.questions = this.steps.filter((s) => s.dataset.quizStep.startsWith('q'));
      this.order = this.buildOrder();
      this.answers = this.load();
      this.countEl = this.querySelector('[data-quiz-count]');
      this.progEl = this.querySelector('[data-quiz-progress]');
      this.backBtn = this.querySelector('[data-quiz-back]');
      this.email = this.querySelector('[data-quiz-email]');
      this.restore();
      this.bind();
      const fromHash = this.stepFromHash();
      this.show(fromHash || 'start', { focus: false, push: false });
      window.addEventListener('popstate', () => this.show(this.stepFromHash() || 'start', { push: false, back: true }));
    }
    buildOrder() {
      const order = ['start'];
      const info = this.steps.filter((s) => s.dataset.quizStep === 'info');
      this.questions.forEach((q) => {
        order.push(q.dataset.quizStep);
        info.filter((i) => i.dataset.after === q.dataset.key).forEach(() => order.push('info'));
      });
      order.push('loader');
      return order;
    }
    load() { try { return JSON.parse(sessionStorage.getItem(STORE)) || {}; } catch (e) { return {}; } }
    save() { try { sessionStorage.setItem(STORE, JSON.stringify(this.answers)); } catch (e) { /* private mode */ } }
    restore() {
      this.questions.forEach((q) => {
        const v = this.answers[q.dataset.key];
        if (v == null) return;
        const vals = Array.isArray(v) ? v : [v];
        q.querySelectorAll('input').forEach((i) => { i.checked = vals.includes(i.value); });
        this.sync(q);
      });
    }
    stepFromHash() {
      const h = window.location.hash;
      const m = h.match(/^#frage-(\d+)$/);
      if (m) { const q = this.questions[Number(m[1]) - 1]; return q ? q.dataset.quizStep : null; }
      if (h === '#wusstest-du') return 'info';
      if (h === '#ergebnis' && this.answers.moment) return `result-${this.answers.moment}`;
      return null;
    }
    hashFor(id) {
      if (id.startsWith('q')) return `#frage-${id.slice(1)}`;
      if (id === 'info') return '#wusstest-du';
      if (id.startsWith('result')) return '#ergebnis';
      return ' ';
    }
    bind() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-quiz-start]')) { this.go('next'); return; }
        const next = e.target.closest('[data-quiz-next]');
        if (next) { if (next.getAttribute('aria-disabled') === 'true') return; this.go('next'); return; }
        if (e.target.closest('[data-quiz-skip]')) {
          const step = e.target.closest('[data-quiz-step]');
          step.querySelectorAll('input').forEach((i) => { i.checked = false; i.disabled = false; });
          this.answers[step.dataset.key] = [];
          this.save();
          this.go('next');
          return;
        }
        if (e.target.closest('[data-quiz-back]')) { window.history.back(); return; }
        if (e.target.closest('[data-quiz-restart]')) {
          this.answers = {};
          try { sessionStorage.removeItem(STORE); } catch (err) { /* ignore */ }
          this.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((i) => { i.checked = false; i.disabled = false; });
          this.questions.forEach((q) => this.sync(q));
          this.show('start', { push: true });
        }
      });
      this.addEventListener('change', (e) => {
        const input = e.target;
        const step = input.closest('[data-quiz-step]');
        if (!step || !step.dataset.key) return;
        if (step.dataset.type === 'multi') {
          if (input.checked && input.hasAttribute('data-exclusive')) step.querySelectorAll('input').forEach((i) => { if (i !== input) i.checked = false; });
          else if (input.checked) { const ex = step.querySelector('[data-exclusive]'); if (ex) ex.checked = false; }
          this.answers[step.dataset.key] = [...step.querySelectorAll('input:checked')].map((i) => i.value);
        } else {
          this.answers[step.dataset.key] = input.value;
        }
        this.save();
        this.sync(step);
      });
      /* single choice: advance 250 ms after a tap or mouse click, also when the tapped answer was already selected
         (restored state). Keyboard changes (arrow keys, space) only enable „Weiter", so every option stays reachable
         and Enter or Space on „Weiter" advances (03 §8.1, A6-03). */
      this.lastPointer = -1;
      this.addEventListener('pointerdown', (e) => { if (e.target.closest('.soi-quiz__opt')) this.lastPointer = performance.now(); });
      this.addEventListener('click', (e) => {
        const input = e.target.closest('input[type="radio"]');
        if (!input) return;
        const step = input.closest('[data-quiz-step]');
        if (!step || step.dataset.type === 'multi') return;
        const fromPointer = performance.now() - this.lastPointer < 1000;
        if (!fromPointer) return;
        this.lastPointer = -1;
        clearTimeout(this.adv);
        this.adv = setTimeout(() => { if (this.current === step.dataset.quizStep && input.checked) this.go('next'); }, 250);
      });
    }
    sync(step) {
      const max = Number(step.dataset.max || 1);
      const inputs = [...step.querySelectorAll('input')];
      const checked = inputs.filter((i) => i.checked);
      if (step.dataset.type === 'multi' && max > 1) inputs.forEach((i) => { i.disabled = !i.checked && checked.length >= max && !i.hasAttribute('data-exclusive'); });
      const next = step.querySelector('[data-quiz-next]');
      if (next) next.setAttribute('aria-disabled', checked.length || step.hasAttribute('data-optional') ? 'false' : 'true');
    }
    go(dir) {
      const i = this.order.indexOf(this.current);
      let id = this.order[Math.min(i + 1, this.order.length - 1)];
      if (this.current && this.current.startsWith('result')) id = 'start';
      this.show(id, { push: true });
    }
    show(id, opts = {}) {
      if (id === 'loader' || (id && id.startsWith('result'))) {
        if (!this.answers.moment) id = 'q1';
      }
      if (id && id.startsWith('q')) {
        const need = this.questions.findIndex((q) => q.dataset.quizStep === id);
        for (let k = 0; k < need; k++) {
          const q = this.questions[k];
          if (!q.hasAttribute('data-optional') && (this.answers[q.dataset.key] == null || this.answers[q.dataset.key].length === 0)) { id = q.dataset.quizStep; break; }
        }
      }
      const el = this.steps.find((s) => s.dataset.quizStep === id) || this.steps[0];
      this.steps.forEach((s) => { s.hidden = s !== el; s.classList.remove('is-entering', 'is-entering-back'); });
      if (!reduce.matches && this.current) el.classList.add(opts.back ? 'is-entering-back' : 'is-entering');
      this.current = el.dataset.quizStep;
      this.dataset.step = this.current;
      if (opts.push) {
        const h = this.hashFor(this.current);
        if (h.trim()) window.history.pushState({ q: this.current }, '', h);
        else window.history.pushState({ q: 'start' }, '', window.location.pathname + window.location.search);
      }
      this.chrome();
      if (this.current === 'loader') this.runLoader();
      if (this.current.startsWith('result')) this.renderResult(el);
      this.resultBar(this.current.startsWith('result') ? el : null);
      if (this.email) {
        this.email.hidden = !this.current.startsWith('result');
        if (!this.email.hidden) el.querySelector('.soi-quiz__card').appendChild(this.email);
      }
      if (opts.focus !== false) {
        const f = el.querySelector('[data-quiz-focus]');
        if (f) f.focus({ preventScroll: true });
        window.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' });
      }
    }
    chrome() {
      const total = this.cfg.total || this.questions.length;
      const q = this.questions.find((s) => s.dataset.quizStep === this.current);
      let idx = 0;
      if (q) idx = Number(q.dataset.index);
      else if (this.current === 'info') idx = Number((this.questions.find((s) => s.dataset.key === (this.steps.find((x) => x.dataset.quizStep === 'info') || {}).dataset?.after) || {}).dataset?.index || 0);
      else if (this.current === 'loader' || this.current.startsWith('result')) idx = total;
      if (this.countEl) this.countEl.textContent = q ? `${idx} von ${total}` : '';
      if (this.progEl) this.progEl.style.setProperty('--p', String(this.current === 'start' ? 0 : Math.min(idx / total, 1)));
      if (this.backBtn) { const off = this.current === 'start'; this.backBtn.classList.toggle('is-off', off); this.backBtn.tabIndex = off ? -1 : 0; this.backBtn.setAttribute('aria-hidden', off ? 'true' : 'false'); }
    }
    runLoader() {
      const ticks = [...this.querySelectorAll('.soi-quiz__tick')];
      ticks.forEach((t) => t.classList.remove('is-done'));
      const dur = 1600;
      ticks.forEach((t, k) => setTimeout(() => t.classList.add('is-done'), reduce.matches ? 0 : ((k + 1) * dur) / (ticks.length + 1)));
      clearTimeout(this.loadT);
      this.loadT = setTimeout(() => {
        if (this.current !== 'loader') return;
        const door = this.answers.moment || 'schlaf';
        this.show(`result-${door}`, { push: true });
      }, dur);
    }
    renderResult(el) {
      const door = el.dataset.door;
      const a = this.answers;
      const duft = Array.isArray(a.duft) ? a.duft : [];
      const type = duft[0] || this.cfg.defaults[door] || 'blumig';
      const t = this.cfg.types[type] || { name: type, caption: '' };
      if (door === 'geschenk') {
        el.querySelectorAll('[data-if-type]').forEach((s) => { s.hidden = !duft.includes(s.dataset.ifType); });
        return;
      }
      const strong = door === 'ruhe' && a.intensitaet === 'kraeftig';
      const txt = el.querySelector('[data-res-text]');
      if (txt) {
        const raw = (strong && txt.dataset.textStrong) ? txt.dataset.textStrong : txt.dataset.text;
        txt.textContent = (raw || '').replace('[Duft-Typ]', this.typePhrase(type));
      }
      const nameEl = el.querySelector('[data-res-type-name]');
      if (nameEl) nameEl.textContent = t.name;
      const cap = el.querySelector('[data-res-type-cap]');
      if (cap) cap.textContent = t.caption || '';
      const stops = ['frisch', 'zitrus', 'blumig', 'warm', 'erdig'];
      const pos = (v) => `${(stops.indexOf(v) + 0.5) * 20}%`;
      const m1 = el.querySelector('[data-res-mark]');
      if (m1) m1.style.setProperty('--x', pos(type));
      const m2 = el.querySelector('[data-res-mark2]');
      if (m2) { m2.hidden = !duft[1]; if (duft[1]) m2.style.setProperty('--x', pos(duft[1])); }

      let picks = (this.cfg.picks[`${door}:${type}`] || []).slice();
      if (strong && this.cfg.ruheStrong && picks.length) picks[0] = this.cfg.ruheStrong;
      const chips = el.querySelector('[data-res-chips]');
      if (chips) {
        chips.textContent = '';
        /* the pick table can repeat a scent (03 §8.4): show it once with a count („2× Sukoon"), keep pick= unchanged */
        const counts = new Map();
        picks.forEach((h) => counts.set(h, (counts.get(h) || 0) + 1));
        counts.forEach((n, h) => { const s = document.createElement('span'); s.textContent = (n > 1 ? `${n}\u00d7 ` : '') + (this.cfg.names[h] || h); chips.appendChild(s); });
      }
      const summary = el.querySelector('[data-res-summary]');
      if (summary) {
        summary.textContent = '';
        ['zeit', 'ort', 'intensitaet'].forEach((k) => {
          const q = this.questions.find((s) => s.dataset.key === k);
          const inp = q && q.querySelector('input:checked');
          if (inp) { const li = document.createElement('li'); li.textContent = inp.closest('label').querySelector('.soi-quiz__opt-l').textContent.trim(); summary.appendChild(li); }
        });
      }
      const safety = Array.isArray(a.sicherheit) ? a.sicherheit : [];
      const map = { schwanger: 'pregnancy', kinder: 'children', haut: 'skin', nuss: 'nut' };
      el.querySelectorAll('[data-note]').forEach((n) => {
        const on = n.hasAttribute('data-always') || safety.some((s) => map[s] === n.dataset.note);
        n.hidden = !on;
      });
      el.querySelectorAll('[data-extra]').forEach((x) => {
        const k = x.dataset.extra;
        x.hidden = !((k === 'massage' && (a.format === 'massageoel' || a.ort === 'bad')) || (k === 'eo' && a.format === 'oel'));
      });
      const cta = el.querySelector('[data-res-cta]');
      const base = this.cfg.doors[door];
      if (cta && base) {
        try {
          const u = new URL(base, window.location.origin);
          u.searchParams.set('soi_src', 'quiz');
          cta.setAttribute('href', u.pathname + u.search.replace(/%2C/g, ','));
        } catch (e) { /* keep server href */ }
      }
    }
    /* ≤ 767 result bar (03 §8.5, A6-R4-01): carries the in-card CTA's computed href into the thumb zone and hides
       while that CTA is at least 50 % visible; off on every other step (the step itself is hidden then) */
    resultBar(el) {
      if (this.barIo) { this.barIo.disconnect(); this.barIo = null; }
      this.querySelectorAll('[data-res-bar]').forEach((b) => { b.hidden = true; b.classList.remove('is-on'); });
      if (!el) return;
      const bar = el.querySelector('[data-res-bar]');
      const cta = el.querySelector('[data-res-cta]');
      const link = bar && bar.querySelector('[data-res-bar-cta]');
      if (!bar || !cta || !link) return;
      link.setAttribute('href', cta.getAttribute('href'));
      bar.hidden = false;
      bar.classList.add('is-on');
      /* it also steps aside once the footnote legend or the legal footer scrolls in, so their lines stay readable */
      const tail = [document.getElementById('soi-footnotes'), document.querySelector('[data-presell-footer]')].filter(Boolean);
      let ctaHalf = false; const tailIn = new Set();
      this.barIo = new IntersectionObserver((es) => {
        es.forEach((e) => {
          if (e.target === cta) ctaHalf = e.isIntersecting && e.intersectionRatio >= 0.5;
          else if (e.isIntersecting) tailIn.add(e.target); else tailIn.delete(e.target);
        });
        bar.classList.toggle('is-on', !ctaHalf && tailIn.size === 0);
      }, { threshold: [0, 0.5, 1] });
      this.barIo.observe(cta);
      tail.forEach((t) => this.barIo.observe(t));
    }
    typePhrase(type) {
      return ({ blumig: 'weiche, blumige Düfte', frisch: 'frische, klare Düfte', zitrus: 'helle, zitrische Düfte', warm: 'warme, würzige Düfte', erdig: 'erdige, ruhige Düfte' })[type] || 'deine Düfte';
    }
  }
  if (!customElements.get('soi-quiz')) customElements.define('soi-quiz', SoiQuiz);
})();
